import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { Breadcrumbs } from '../../components/Breadcrumbs'
import { apiErrorMessage } from '../../lib/errors'
import { formatDateTime } from '../../lib/format'
import {
  fetchRateConfirmationDocument,
  getRateConfirmation,
  readRateConfirmationAgain,
  type RateConfirmationDetail,
} from './api'
import { AttemptsTable } from './AttemptsTable'
import { flagsByField, statusTone, type FlagsByField } from './describe'
import { DocumentReading } from './DocumentReading'
import { ReviewPanel } from './ReviewPanel'

/** How often the page asks again while the file is being read — a reading takes seconds. */
export const REFRESH_MS = 3_000

/** A document the review has no flags for. */
const NO_FLAGS: FlagsByField = new Map()

/** How long an opened PDF's address lives: long enough for the new tab to take it. */
const PDF_URL_LIFETIME_MS = 60_000

/**
 * One rate confirmation (Stage 3, 3.1): the file, where its reading stands, what was read —
 * field by field with its evidence — the platform's review, and every reading's cost. While
 * the file is being read the page asks again every few seconds, and stops once it is not.
 *
 * <p>
 * Keyed by the record, so another record's page starts over: its view, its confirmation and
 * its failures belong to it.
 * </p>
 */
export function RateConfirmationPage() {
  const { id } = useParams()
  return id === undefined ? null : <RecordPage key={id} id={id} />
}

function RecordPage({ id }: { id: string }) {
  const { t, i18n } = useTranslation()
  const language = i18n.language

  const [record, setRecord] = useState<RateConfirmationDetail | null>(null)
  const [failure, setFailure] = useState<unknown>(null)
  const [actionFailure, setActionFailure] = useState<{ error: unknown; fallbackKey: string } | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [reading, setReading] = useState(0)
  // Every reading of the record that finished, failed ones included — what the next one waits on.
  const [settled, setSettled] = useState(0)

  useEffect(() => {
    let active = true

    getRateConfirmation(id)
      .then((found) => {
        if (active) {
          setRecord(found)
          setFailure(null)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setFailure(caught)
        }
      })
      .finally(() => {
        if (active) {
          setSettled((current) => current + 1)
        }
      })

    return () => {
      active = false
    }
  }, [id, reading])

  // While the file is being read, ask again after every answer — a failed one included (the
  // broadcast page's lesson, Copilot on #44) — and stop once it is not.
  const isReading = record?.status === 'Reading'
  useEffect(() => {
    if (!isReading) {
      return
    }

    const timer = window.setTimeout(() => setReading((current) => current + 1), REFRESH_MS)
    return () => window.clearTimeout(timer)
  }, [isReading, settled])

  // By the document each review is about, never by position in the list.
  const flagMaps = useMemo(
    () => new Map((record?.review ?? []).map((review) => [review.index, flagsByField(review.flags)] as const)),
    [record],
  )

  if (record === null) {
    return failure === null ? (
      <p className="session-check">{t('rateConfirmations.loadingOne')}</p>
    ) : (
      <section className="page">
        <Breadcrumbs trail={[{ label: t('nav.rateConfirmations'), to: '/rate-confirmations' }, { label: null }]} />
        <p className="alert" role="alert">
          {apiErrorMessage(failure, t, 'rateConfirmations.loadOneError')}
        </p>
      </section>
    )
  }

  /**
   * Opens the PDF in a tab of its own. The tab is opened in the click itself and given the
   * file once it arrives: a tab opened after the wait would be a popup the browser blocks. A
   * blocked tab gets the file as a download instead, so the click always ends with the PDF.
   */
  async function openPdf(current: RateConfirmationDetail) {
    setActionFailure(null)
    const tab = window.open('', '_blank')

    try {
      const url = URL.createObjectURL(await fetchRateConfirmationDocument(current.id))

      if (tab === null) {
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = current.fileName
        anchor.click()
      } else {
        tab.location.href = url
      }

      window.setTimeout(() => URL.revokeObjectURL(url), PDF_URL_LIFETIME_MS)
    } catch (caught) {
      tab?.close()
      setActionFailure({ error: caught, fallbackKey: 'rateConfirmations.pdfError' })
    }
  }

  async function readAgain(current: RateConfirmationDetail) {
    setConfirming(false)
    setActionFailure(null)
    setBusy(true)

    try {
      setRecord(await readRateConfirmationAgain(current.id))
    } catch (caught) {
      setActionFailure({ error: caught, fallbackKey: 'rateConfirmations.readAgainError' })
      // Whatever the refusal says, the screen must show where the record stands now.
      setReading((count) => count + 1)
    } finally {
      setBusy(false)
    }
  }

  const documents = record.reading?.documents ?? []
  const subtitle = [
    t('rateConfirmations.receivedOn', { when: formatDateTime(record.receivedAt, language) }),
    record.receivedByEmail === null ? null : t('rateConfirmations.receivedBy', { email: record.receivedByEmail }),
    (record.reading?.pageCount ?? null) === null ? null : t('rateConfirmations.pages', { count: record.reading?.pageCount }),
  ].filter((part) => part !== null)

  return (
    <section className="page">
      <Breadcrumbs
        trail={[
          { label: t('nav.rateConfirmations'), to: '/rate-confirmations' },
          { label: record.fileName },
        ]}
      />
      <header className="page__header page__header--row">
        <div className="rc-heading">
          <h1 className="rc-title">{record.fileName}</h1>
          <p className="page__subtitle">{subtitle.join(' · ')}</p>
        </div>
        <div className="page__actions">
          <button type="button" className="button button--quiet" onClick={() => void openPdf(record)}>
            {t('rateConfirmations.openPdf')}
          </button>
          {!isReading && (
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              // A reading on file is replaced by the careful one, so asking says so first;
              // a failed record has nothing to lose.
              onClick={() => (record.status === 'ReadyForReview' ? setConfirming(true) : void readAgain(record))}
            >
              {t('rateConfirmations.readAgain')}
            </button>
          )}
        </div>
      </header>

      <p className="rc-status" role="status">
        <span className={`badge badge--${statusTone(record.status)}`}>{t(`rateConfirmations.statuses.${record.status}`)}</span>{' '}
        {t(`rateConfirmations.statusNotes.${record.status}`)}
      </p>

      {record.status === 'ReadFailed' && record.failure !== null && (
        <div className="alert" role="alert">
          <p>{t(`rateConfirmations.outcomes.${record.failure}`)}</p>
          {record.failureDetail !== null && <p className="rc-failure-detail">{record.failureDetail}</p>}
        </div>
      )}

      {confirming && (
        <div className="alert alert--warning rc-confirm" role="group" aria-label={t('rateConfirmations.readAgain')}>
          <p>{t('rateConfirmations.readAgainConfirm')}</p>
          <div className="form__actions">
            <button type="button" className="button" disabled={busy} onClick={() => void readAgain(record)}>
              {t('rateConfirmations.readAgainYes')}
            </button>
            <button type="button" className="button button--quiet" onClick={() => setConfirming(false)}>
              {t('rateConfirmations.cancel')}
            </button>
          </div>
        </div>
      )}

      {actionFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(actionFailure.error, t, actionFailure.fallbackKey)}
        </p>
      )}

      {isReading && failure !== null && (
        <p className="muted" role="status">
          {t('rateConfirmations.refreshFailed')}
        </p>
      )}

      {record.reading !== null && documents.length === 0 && (
        <p className="placeholder">{t('rateConfirmations.noDocuments')}</p>
      )}

      {documents.map((document, index) => {
        const review = record.review?.find((each) => each.index === index)

        return (
          <article className="rc-document" key={`document-${index}`}>
            <h2 className="rc-document__title">
              {documents.length > 1
                ? t('rateConfirmations.documentOf', { number: index + 1, count: documents.length })
                : t('rateConfirmations.document')}
              {document.loadNumber.value !== null && ` · ${t('rateConfirmations.loadNumber', { number: document.loadNumber.value })}`}
              {document.firstPage !== null && (
                <span className="rc-document__pages">
                  {' · '}
                  {document.lastPage !== null && document.lastPage !== document.firstPage
                    ? t('rateConfirmations.pageRange', { first: document.firstPage, last: document.lastPage })
                    : t('rateConfirmations.evidence.page', { page: document.firstPage })}
                </span>
              )}
            </h2>
            {review !== undefined && <ReviewPanel review={review} document={document} />}
            <DocumentReading document={document} flags={flagMaps.get(index) ?? NO_FLAGS} />
          </article>
        )
      })}

      {record.attempts.length > 0 && <AttemptsTable attempts={record.attempts} />}
    </section>
  )
}
